import React, { useMemo, useRef, useState, useEffect } from 'react';

// Web MRR line chart — mirrors the mobile FounderIntelligence RevenueChart:
// area-filled solid lime history + dashed gold 6-month projection, with a
// hover scrubber + tooltip. Designed to sit on a dark forest card.
const C = { lime: '#B4D330', gold: '#C9A85B', cream: '#FAF6EC', forestDeep: '#0F2D1F' };
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;

export default function RevenueChart({ series = [], projection = [] }) {
  const W = 640, H = 172, padT = 16, padB = 26, padL = 8, padR = 8;
  const [active, setActive] = useState(null);
  const [drawn, setDrawn] = useState(false);
  const svgRef = useRef(null);

  const all = useMemo(() => {
    const hist = (series || []).map((p) => ({ ...p, projected: false }));
    const proj = (projection || []).map((p) => ({ ...p, projected: true }));
    return (hist.length ? [...hist, ...proj] : proj).map((p, i) => ({ ...p, i }));
  }, [series, projection]);

  useEffect(() => { setDrawn(false); const t = setTimeout(() => setDrawn(true), 40); return () => clearTimeout(t); }, [all]);

  const n = all.length;
  const histCount = (series || []).length;
  const maxV = Math.max(...all.map((p) => p.mrr || 0), 1) * 1.15;
  const chartW = W - padL - padR;
  const step = n > 1 ? chartW / (n - 1) : chartW;
  const x = (i) => padL + step * i;
  const y = (v) => padT + (H - padT - padB) * (1 - (v || 0) / maxV);
  const line = (pts) => pts.map((p, k) => `${k === 0 ? 'M' : 'L'} ${x(p.i).toFixed(1)} ${y(p.mrr).toFixed(1)}`).join(' ');
  const histPts = all.slice(0, histCount);
  const projPts = histCount > 0 ? all.slice(histCount - 1) : all;
  const areaPath = histPts.length
    ? `${line(histPts)} L ${x(histPts[histPts.length - 1].i).toFixed(1)} ${(H - padB).toFixed(1)} L ${x(histPts[0].i).toFixed(1)} ${(H - padB).toFixed(1)} Z`
    : '';

  const act = active != null ? all[active] : null;
  const histLen = 1400;

  const onMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    if (n <= 1) return setActive(0);
    const i = Math.round((px - padL) / step);
    setActive(Math.max(0, Math.min(n - 1, i)));
  };

  if (!n) return null;

  return (
    <div style={{ marginTop: 12, position: 'relative' }} data-testid="founder-revenue-chart">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
        onMouseMove={onMove} onMouseLeave={() => setActive(null)} style={{ display: 'block', cursor: 'crosshair' }}>
        <defs>
          <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={C.lime} stopOpacity="0.28" />
            <stop offset="1" stopColor={C.lime} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="rgba(250,246,236,0.12)" strokeWidth={1} />
        {areaPath ? <path d={areaPath} fill="url(#revfill)" /> : null}
        {projPts.length > 1 ? (
          <path d={line(projPts)} stroke={C.gold} strokeWidth={2.5} fill="none" strokeDasharray="6 5" strokeLinecap="round"
            opacity={drawn ? 1 : 0} style={{ transition: 'opacity 0.6s ease 0.6s' }} />
        ) : null}
        {histPts.length > 1 ? (
          <path d={line(histPts)} stroke={C.lime} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={histLen} strokeDashoffset={drawn ? 0 : histLen}
            style={{ transition: 'stroke-dashoffset 1.1s cubic-bezier(0.22,1,0.36,1)' }} />
        ) : null}
        {act ? (
          <g>
            <line x1={x(act.i)} y1={padT} x2={x(act.i)} y2={H - padB} stroke="rgba(250,246,236,0.35)" strokeWidth={1} strokeDasharray="3 3" />
            <circle cx={x(act.i)} cy={y(act.mrr)} r={5.5} fill={act.projected ? C.gold : C.lime} stroke={C.forestDeep} strokeWidth={2} />
          </g>
        ) : null}
        {all.map((p, i) => (
          (i % 2 === 0 || i === n - 1) ? (
            <text key={i} x={x(i)} y={H - 8} fill={p.projected ? 'rgba(201,168,91,0.85)' : 'rgba(250,246,236,0.55)'} fontSize="10" textAnchor="middle">{p.label}</text>
          ) : null
        ))}
      </svg>
      {act ? (
        <div style={{ position: 'absolute', top: 0, left: `${Math.max(1, Math.min(78, (x(act.i) / W) * 100 - 9))}%`, background: C.forestDeep, border: '1px solid rgba(180,211,48,0.3)', borderRadius: 10, padding: '6px 10px', pointerEvents: 'none' }} data-testid="founder-chart-tooltip">
          <p style={{ fontSize: 11, color: 'rgba(250,246,236,0.7)', margin: 0 }}>{act.label}{act.projected ? ' · projected' : ''}</p>
          <p style={{ fontSize: 15, fontWeight: 700, color: act.projected ? C.gold : C.lime, margin: 0, fontFamily: 'Outfit, sans-serif' }}>{money0(act.mrr)}</p>
        </div>
      ) : null}
    </div>
  );
}
